import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { http } from '../common/http';
import '../common/components/utils/dayjs';

import { Navigation } from './components/Navigation';
import { Dropdown } from '../common/components/Dropdown';
import { Tooltip } from './components/Tooltip';
import { Tabs } from './components/Tabs';
import { Modal } from '../common/Modal';
import { FlashMessage } from './components/FlashMessage';
import { Video } from './components/Video';
import { Accordion } from './components/Accordion';
import { UploadImage } from './components/UploadImage';
import { RevealInput } from './components/RevealInput';

import VUploadList from './components/VUploadList.vue';
import VUploadItem from './components/VUploadItem.vue';
import VMedicalDocumentsUploadList from './components/medical-documents/VUploadList.vue';
import VMedicalDocumentsUploadItem from './components/medical-documents/VUploadItem.vue';

import VTextEditor from './components/VTextEditor.vue';
import VTimeRange from './components/VTimeRange.vue';
import VScript from './components/VScript.vue';
import VTreatmentDaysAndShifts from '../common/components/booking/VTreatmentDaysAndShifts.vue';
import VSingleTreatment from './components/offline-booking/VSingleTreatment.vue';
import VWeeklyTreatment from './components/offline-booking/VWeeklyTreatment.vue';
import VTreatmentTime from './components/offline-booking/VTreatmentTime.vue';
import VBookingTotal from './components/booking/VBookingTotal.vue';
import VInputStepper from './components/VInputStepper.vue';
import VSelectSearch from './components/VSelectSearch.vue';
import VPermanentPatient from './components/booking/VPermanentPatient.vue';
import UpdateTimeframe from './pages/UpdateTimeframe.vue';
import VOfflineBooking from './components/offline-booking/VOfflineBooking.vue';
import VReviews from './components/VReviews.vue';

import { DropdownCheckList } from './components/DropdownCheckList';
import { CalendarGroup } from './components/CalendarGroup';
import { SearchFilters } from './components/SearchFilters';
import { SliderGroup } from './components/SliderGroup';

import { onTabsChange } from './pages/home';
import { CalendarSearch } from './components/CalendarSearch';
import { Lists } from '../common/components/Lists';

import createServer from './mock';
import { scrollIntoView } from './utils/scrollIntoView';
import { DeclineBooking } from './components/declineBooking';
import { ElementCollapse } from '../common/components/ElementCollapse';
import { CountriesSlider } from './components/countriesSlider';
import { TestimonialsSlider } from './components/testimonialsSlider';
import { PartnersSlider } from './components/partnersSlider';
import { googleAnalytics } from './components/googleAnalytics';
import { PhoneCountryCode } from './components/phoneCountryCode';
import { SmartBanner } from './components/SmartBanner';
import { MedicalDocuments } from './components/medical-documents/MedicalDocuments';
import { EaseIntro } from './components/EaseIntro';
import { EaseUploadSuccess } from './components/EaseUploadSuccess';
import { InputCounter } from './components/InputCounter';
import { ResendCode } from './components/ResendCode';
import { VerificationCode } from './components/VerificationCode';
import { ClinicReview } from './components/clinicReview';
import '../common/_icons';

if (Number(process.env.MIX_MOCK_SERVER)) {
	createServer();
}

// define map variable before initialization
window.MAP_STYLE = [
	{
		featureType: 'all',
		elementType: 'labels.text',
		stylers: [
			{
				color: '#878787',
			},
		],
	},
	{
		featureType: 'all',
		elementType: 'labels.text.stroke',
		stylers: [
			{
				visibility: 'off',
			},
		],
	},
	{
		featureType: 'administrative',
		elementType: 'labels.text.fill',
		stylers: [
			{
				color: '#393E41',
			},
		],
	},
	{
		featureType: 'landscape',
		elementType: 'all',
		stylers: [
			{
				color: '#F9F5ED',
			},
		],
	},
	{
		featureType: 'road.highway',
		elementType: 'all',
		stylers: [
			{
				color: '#F5F5F5',
			},
		],
	},
	{
		featureType: 'road.highway',
		elementType: 'geometry.stroke',
		stylers: [
			{
				color: '#C9C9C9',
			},
		],
	},
	{
		featureType: 'water',
		elementType: 'all',
		stylers: [
			{
				color: '#AEE0F4',
			},
		],
	},
];

window.scrollIntoView = scrollIntoView;

// initialize Vue components
const vueWrapper = [...document.querySelectorAll('.vue-app-wrapper')];

window.http = http;

if (vueWrapper.length) {
	const store = createPinia();
	vueWrapper.forEach((appEl) => {
		const app = createApp({})
			.component('v-treatment-days-and-shifts', VTreatmentDaysAndShifts)
			.component('v-treatment-time', VTreatmentTime)
			.component('v-single-treatment', VSingleTreatment)
			.component('v-weekly-treatment', VWeeklyTreatment)
			.component('v-booking-total', VBookingTotal)
			.component('v-upload-list', VUploadList)
			.component('v-upload-item', VUploadItem)
			.component('v-medical-documents-upload-list', VMedicalDocumentsUploadList)
			.component('v-medical-documents-upload-item', VMedicalDocumentsUploadItem)
			.component('v-input-stepper', VInputStepper)
			.component('v-select-search', VSelectSearch)
			.component('v-update-timeframe', UpdateTimeframe)
			.component('v-offline-booking', VOfflineBooking)
			.component('v-text-editor', VTextEditor)
			.component('v-time-range', VTimeRange)
			.component('v-script', VScript)
			.component('v-reviews', VReviews)
			.component('v-permanent-patient', VPermanentPatient)
			.use(store);

		// Add window to Vue 3
		app.config.globalProperties.window = window;
		app.mount(appEl);
	});
}

Modal.init();
Tooltip.init();
Dropdown.init();
DropdownCheckList.init();
Navigation.init();
CalendarSearch.init();
CalendarGroup.init();
SliderGroup.init();
FlashMessage.init();
Video.init();
InputCounter.init();
ResendCode.init();
VerificationCode.init();

Accordion.init();
SearchFilters.init();
Tabs.init({ onChange: onTabsChange });
UploadImage.init();
RevealInput.init();
Lists.init();

DeclineBooking.init();
ElementCollapse.init();
CountriesSlider.init();
TestimonialsSlider.init();
PartnersSlider.init();
googleAnalytics.init();
PhoneCountryCode.init();
SmartBanner.init();
MedicalDocuments.init();
EaseIntro.init();
EaseUploadSuccess.init();
ClinicReview.init();

const page = document.querySelector('[data-page-name]');
const currentPageName = page?.dataset?.pageName;

if (currentPageName === 'autocomplete') {
	import(/* webpackChunkName: "autocomplete" */ './pages/autocomplete').then((module) => {
		const { init } = module;
		init();
	});
}

if (currentPageName === 'clinic-search') {
	import(/* webpackChunkName: "clinic-search" */ './pages/search').then((module) => {
		const { init } = module;
		init();
	});
}

if (currentPageName === 'clinic-profile') {
	import(/* webpackChunkName: "clinic-profile" */ './pages/clinic-profile').then((module) => {
		const { init } = module;
		init();
	});
}

if (currentPageName === 'booking') {
	import(/* webpackChunkName: "booking" */ './components/booking/Booking').then((module) => {
		const { Booking } = module;
		Booking.init();
	});
}

if (currentPageName === 'home') {
	import(/* webpackChunkName: "home" */ './pages/home').then((module) => {
		const { init } = module;
		init();
	});
}

if (currentPageName === 'book-treatment') {
	import(/* webpackChunkName: "book-treatment" */ './pages/book-treatment').then((module) => {
		const { init } = module;
		init();
	});
}

// load stripe.js where needed
if (currentPageName === 'add-credit-card') {
	import(/* webpackChunkName: "add-credit-card" */ './components/addCreditCardForm').then(
		(module) => {
			const { AddCreditCardForm } = module;
			AddCreditCardForm.init();
		}
	);
}

if (currentPageName === 'create-payment') {
	import(/* webpackChunkName: "create-payment" */ './components/createPayment').then((module) => {
		const { CreatePaymentForm } = module;
		CreatePaymentForm.init();
	});
}
